import { useCallback, useEffect, useRef, useState } from "react";
import {
    useDataChannel,
    useLocalParticipant,
    useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";

import {
    BoardMessage,
    BoardPoint,
    BoardStroke,
    POINT_BATCH_MS,
    WHITEBOARD_TOPIC,
    chunkStrokes,
    mayClearBoard,
    shouldAnswerSync,
} from "../domain/whiteboard";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export type WhiteboardChannel = {
    /** Every stroke the board is known to hold, local and remote alike. */
    strokes: BoardStroke[];
    localIdentity: string;
    /** True while something is failing to reach the other side. */
    transportFailed: boolean;
    /**
     * Whether the board is up on this screen.
     *
     * Shared state rather than the panel's own, because raising the board is
     * something one person does *to the call*: it belongs with the strokes,
     * not with whichever component happens to be drawing them.
     */
    open: boolean;
    /** Put the board up, here and on the other side. */
    openBoard: () => void;
    /** Put the board away here only. The other person keeps theirs. */
    closeBoard: () => void;
    publishStrokeStart: (stroke: BoardStroke) => void;
    publishStrokePoints: (strokeId: string, point: BoardPoint) => void;
    commitLocalStroke: (stroke: BoardStroke) => void;
    clearBoard: () => void;
};

function appendPoints(
    strokes: BoardStroke[],
    strokeId: string,
    points: BoardPoint[]
): BoardStroke[] {
    return strokes.map((stroke) =>
        stroke.id === strokeId
            ? { ...stroke, points: [...stroke.points, ...points] }
            : stroke
    );
}

function mergeStrokes(
    existing: BoardStroke[],
    incoming: BoardStroke[]
): BoardStroke[] {
    const byId = new Map(existing.map((stroke) => [stroke.id, stroke]));
    for (const stroke of incoming) {
        byId.set(stroke.id, stroke);
    }
    return Array.from(byId.values());
}

/**
 * The board's transport: strokes in and out over the meeting's existing
 * LiveKit data channel.
 *
 * Nothing here touches the server. A board only becomes a record when the
 * therapist saves it, which is a separate, deliberate action.
 *
 * Call this above whatever shows the board, not inside it. The strokes live
 * in this hook's state, so unmounting it is what loses the board — and a
 * board that vanished because someone glanced back at the faces would be
 * worse than no board at all.
 */
export function useWhiteboardChannel(): WhiteboardChannel {
    const [strokes, setStrokes] = useState<BoardStroke[]>([]);
    const [transportFailed, setTransportFailed] = useState(false);
    const [open, setOpen] = useState(false);

    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    const localIdentity = localParticipant?.identity ?? "";

    // Read by callbacks that must see the board as it is now rather than as
    // it was when they were created. Written in an effect, never in render.
    const strokesRef = useRef<BoardStroke[]>([]);
    useEffect(() => {
        strokesRef.current = strokes;
    }, [strokes]);

    // Read from inside `answerSync`, which is a callback rather than a render.
    const openRef = useRef(false);
    useEffect(() => {
        openRef.current = open;
    }, [open]);

    // Points collected since the last flush, keyed by the stroke they belong
    // to, so one packet can carry a whole flick of the wrist.
    const pending = useRef<Map<string, BoardPoint[]>>(new Map());
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const sendRef = useRef<
        ((payload: Uint8Array, options: object) => Promise<void> | void) | null
    >(null);

    /**
     * Record whether the channel is carrying traffic, without re-rendering
     * the board on every packet that lands.
     *
     * The state only ever changes on the edge — the first failure, and the
     * first success after one. A `setState` per successful packet would mean
     * a render for every batch of pointer movement, fifty milliseconds apart,
     * for as long as anyone is drawing.
     */
    const failing = useRef(false);
    const markTransport = useCallback((failed: boolean) => {
        if (failing.current === failed) return;
        failing.current = failed;
        setTransportFailed(failed);
    }, []);

    const publish = useCallback((message: BoardMessage) => {
        const send = sendRef.current;
        if (!send) return;
        try {
            // `send` rejects when the channel is not ready or the peer has
            // gone. Left unhandled that is an unhandled rejection and a
            // stroke that silently never arrived, so it is caught and shown.
            const result = send(encoder.encode(JSON.stringify(message)), {
                reliable: true,
                topic: WHITEBOARD_TOPIC,
            });
            if (result && typeof result.then === "function") {
                // Both ways round. The warning used to be one-way, so a
                // single failure left "some strokes may not have arrived"
                // under the board for the rest of the session, long after the
                // channel had recovered.
                result.then(
                    () => markTransport(false),
                    () => markTransport(true)
                );
            }
        } catch {
            markTransport(true);
        }
    }, [markTransport]);

    const answerSync = useCallback(
        (requesterIdentity: string) => {
            const others = Array.from(room?.remoteParticipants?.values() ?? []).map(
                (participant) => participant.identity
            );
            if (!shouldAnswerSync(localIdentity, others, requesterIdentity)) return;

            const isOpen = openRef.current;
            const chunks = chunkStrokes(strokesRef.current);
            // An empty board still answers, so the newcomer stops waiting.
            if (chunks.length === 0) {
                publish({ kind: "sync-chunk", strokes: [], open: isOpen });
                return;
            }
            for (const chunk of chunks) {
                publish({ kind: "sync-chunk", strokes: chunk, open: isOpen });
            }
        },
        [localIdentity, publish, room]
    );

    const onMessage = useCallback(
        (raw: { payload: Uint8Array; from?: { identity: string } }) => {
            let message: BoardMessage;
            try {
                message = JSON.parse(decoder.decode(raw.payload)) as BoardMessage;
            } catch {
                // A malformed packet is not worth taking the board down for.
                return;
            }

            const senderIdentity = raw.from?.identity ?? "";

            switch (message.kind) {
                case "stroke-start":
                    setStrokes((current) => mergeStrokes(current, [message.stroke]));
                    break;
                case "stroke-points":
                    setStrokes((current) =>
                        appendPoints(current, message.strokeId, message.points)
                    );
                    break;
                case "clear":
                    // Enforced here, not only by hiding the control: a packet
                    // is whatever the other browser chose to send.
                    if (mayClearBoard(senderIdentity)) setStrokes([]);
                    break;
                case "sync-request":
                    answerSync(senderIdentity);
                    break;
                case "board-open":
                    setOpen(true);
                    break;
                case "sync-chunk":
                    setStrokes((current) => mergeStrokes(current, message.strokes));
                    if (message.open) setOpen(true);
                    break;
            }
        },
        [answerSync]
    );

    const { send } = useDataChannel(WHITEBOARD_TOPIC, onMessage);
    useEffect(() => {
        sendRef.current = send as unknown as (
            payload: Uint8Array,
            options: object
        ) => Promise<void>;
    }, [send]);

    /**
     * Ask for the board as it stands — on the way in and again after a
     * reconnection, so a patient who dropped out does not come back to a blank
     * canvas the therapist is still drawing on.
     *
     * This used to fire on mount. `LiveKitRoom` renders its children while it
     * is still connecting, so the request went out against a room with no peer
     * connection yet: `publishData` rejected, the board raised its "may not
     * have reached the other person" warning on every single join, and the
     * request itself was never sent — so whoever joined second never received
     * a board that was already being drawn on.
     */
    useEffect(() => {
        if (!room) return;

        const request = () => publish({ kind: "sync-request" });
        if (room.state === ConnectionState.Connected) request();

        room.on(RoomEvent.Connected, request);
        room.on(RoomEvent.Reconnected, request);
        return () => {
            room.off(RoomEvent.Connected, request);
            room.off(RoomEvent.Reconnected, request);
        };
    }, [publish, room]);

    const flush = useCallback(() => {
        flushTimer.current = null;
        for (const [strokeId, points] of pending.current.entries()) {
            if (points.length > 0) {
                publish({ kind: "stroke-points", strokeId, points });
            }
        }
        pending.current.clear();
    }, [publish]);

    useEffect(() => {
        return () => {
            if (flushTimer.current) clearTimeout(flushTimer.current);
        };
    }, []);

    const publishStrokeStart = useCallback(
        (stroke: BoardStroke) => {
            publish({ kind: "stroke-start", stroke });
        },
        [publish]
    );

    const publishStrokePoints = useCallback(
        (strokeId: string, point: BoardPoint) => {
            const points = pending.current.get(strokeId) ?? [];
            points.push(point);
            pending.current.set(strokeId, points);

            if (!flushTimer.current) {
                flushTimer.current = setTimeout(flush, POINT_BATCH_MS);
            }
        },
        [flush]
    );

    const commitLocalStroke = useCallback(
        (stroke: BoardStroke) => {
            // Send the tail of the stroke before it stops being live, then
            // keep it locally so a later full redraw still has it.
            flush();
            setStrokes((current) => mergeStrokes(current, [stroke]));
        },
        [flush]
    );

    /**
     * Raise the board for both people.
     *
     * The other side is told rather than left to notice: a board only the
     * person drawing can see is indistinguishable from a board that is not
     * working, which is exactly how this read before the message existed.
     */
    const openBoard = useCallback(() => {
        setOpen(true);
        publish({ kind: "board-open" });
    }, [publish]);

    /** Local only — see the note on `board-open` in the domain. */
    const closeBoard = useCallback(() => setOpen(false), []);

    const clearBoard = useCallback(() => {
        if (!mayClearBoard(localIdentity)) return;
        pending.current.clear();
        setStrokes([]);
        publish({ kind: "clear" });
    }, [localIdentity, publish]);

    return {
        strokes,
        localIdentity,
        transportFailed,
        open,
        openBoard,
        closeBoard,
        publishStrokeStart,
        publishStrokePoints,
        commitLocalStroke,
        clearBoard,
    };
}
